import axios from 'axios';

// Get config from runtime if available, fallback to process.env
const config = window.RUNTIME_CONFIG || {};
const API_URL = config.REACT_APP_API_URL || process.env.REACT_APP_API_URL;
const API_KEY = config.REACT_APP_API_KEY || process.env.REACT_APP_API_KEY;

export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'x-api-key': API_KEY
  }
});

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      // Auto logout on 401 Unauthorized
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const s3Service = {
  listFolders: async (prefix = '') => {
    try {
      const response = await api.get(`/api/s3/folders?prefix=${prefix}`);
      return response.data;
    } catch (error) {
      console.error('Error listing folders:', error);
      // Return empty folders array to prevent UI crashes
      return { folders: [] };
    }
  },
  
  listEnvFiles: async (prefix = '') => {
    try {
      const response = await api.get(`/api/s3/env-files?prefix=${prefix}`);
      return response.data;
    } catch (error) {
      console.error('Error listing env files:', error);
      // Return empty files array to prevent UI crashes
      return { files: [] };
    }
  },
  
  getEnvFile: async (fileKey) => {
    try {
      const response = await api.get(`/api/s3/env-file/${fileKey}`);
      return response.data;
    } catch (error) {
      console.error(`Error getting env file ${fileKey}:`, error);
      throw error; // Re-throw to allow component to handle it
    }
  },
  
  updateEnvFile: async (fileKey, content) => {
    try {
      const response = await api.put(`/api/s3/env-file/${fileKey}`, content);
      return response.data;
    } catch (error) {
      console.error(`Error updating env file ${fileKey}:`, error);
      throw error; // Re-throw to allow component to handle it
    }
  },
  
  getAuditLogs: async () => {
    try {
      const response = await api.get('/api/s3/audit-logs');
      return response.data;
    } catch (error) {
      console.error('Error getting audit logs:', error);
      throw error;
    }
  }
};

export const userService = {
  getCurrentUser: async () => {
    try {
      const response = await api.get('/api/users/me');
      return response.data;
    } catch (error) {
      console.error('Error getting current user:', error);
      return null;
    }
  },
  
  getAllUsers: async () => {
    try {
      const response = await api.get('/api/users/');
      return response.data;
    } catch (error) {
      console.error('Error getting all users:', error);
      return [];
    }
  },
  
  getPendingUsers: async () => {
    try {
      const response = await api.get('/api/users/pending-approval');
      return response.data;
    } catch (error) {
      console.error('Error getting pending users:', error);
      return [];
    }
  },
  
  toggleAdminStatus: async (userId) => {
    try {
      const response = await api.put(`/api/users/${userId}/toggle-admin`);
      return response.data;
    } catch (error) {
      console.error(`Error toggling admin status for user ${userId}:`, error);
      throw error;
    }
  },
  
  approveUser: async (userId) => {
    try {
      const response = await api.put(`/api/users/${userId}/approve`);
      return response.data;
    } catch (error) {
      console.error(`Error approving user ${userId}:`, error);
      throw error;
    }
  },
  
  rejectUser: async (userId) => {
    try {
      const response = await api.put(`/api/users/${userId}/reject`);
      return response.data;
    } catch (error) {
      console.error(`Error rejecting user ${userId}:`, error);
      throw error;
    }
  },
  
  togglePermissions: async (userId) => {
    try {
      const response = await api.put(`/api/users/${userId}/toggle-permissions`);
      return response.data;
    } catch (error) {
      console.error(`Error toggling permissions for user ${userId}:`, error);
      throw error;
    }
  },
  
  deleteUser: async (userId) => {
    try {
      const response = await api.delete(`/api/users/${userId}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting user ${userId}:`, error);
      throw error;
    }
  }
};
